import { useState, useEffect, useCallback, useRef, useContext } from 'react'
import styled from '@emotion/styled'
import { useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { debounce } from 'lodash'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'
import { useDeepCompareEffect } from 'react-use'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { DatasetsHeader } from '@components/headers/datasets'
import { Status } from '@components/layout/headerNav'
import { Loader } from '@components/loader'
import { PromptSavedChanges } from '@components/modals/promptSavedChanges'
import { usePageTitleContext } from '@contexts'
import { PageHeaderContext } from '@contexts/pageHeader'
import { CreateDatasetContext } from '@datasets/reducer/context'
import { useDatasetsDataCache } from '@global/hooks/queries/useDatasetsDataCache'
import { useTemplate } from '@global/hooks/useTemplate'
import { api } from 'api'
import { validateIntegrationRequirements } from 'dataContainer/validators/integration-validator'
import { showSnackbar } from 'functions'
import { usePasteNotification } from 'handsontable/hooks/usePasteNotification'
import { UrlAudits } from 'handsontable/utils/codes.url-audits'
import { Query as GetTemplatesQueryKey } from 'pages/datasets/types'
import { ROUTES, routeByParam } from 'routes'
import { CreateDatasetToolbar } from './components/toolbar'
import { useEffectPreventReload } from './useEffectPreventReload'
import * as DatasetActions from '../datasets.actions'
import { SubmissionTable } from '../handsontable/submission-table/submission-table'
import { moveToCellWithError } from '../utils'
import { useQueryDatasetData, KEY_DATASET_DATA_QUERY } from './useQueryDatasetData'
import { t } from '@lingui/core/macro'

const TableWrapper = styled.div`
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const useAutoSave = (onSave, unsavedChanges, delay = 300000) => {
  useEffect(() => {
    if (!unsavedChanges) return

    const stop = setInterval(onSave, delay)

    return () => {
      clearInterval(stop)
    }
  }, [onSave, unsavedChanges])
}

export const CreateDataset = () => {
  const { datasetId: dsId, templateId: defaultTemplateId } = useParams()
  const { state, dispatch: dispatchState } = useContext(CreateDatasetContext)
  const history = useHistory()
  const dispatch = useDispatch()
  const tableRef = useRef(null)
  const binderRef = useRef(null)
  const [tableHasData, setTableHasData] = useState(false)
  const [updatedAt, setUpdatedAt] = useState(null)
  const [headerTitleInvalid, setHeaderTitleInvalid] = useState(false)
  const [blockSave, setBlockSave] = useState(false)
  const {
    subTitle,
    templateId,
    unsavedChanges,
    errors,
    blockSubmit,
    isSubmitting,
    isPasting,
    loading,
    dataContainer,
    dataset,
    submissionName,
    integrationErrors,
  } = state

  // Custom Hook to manage copy and paste.
  const { setPasteStatus, setCopyStatus } = usePasteNotification({
    isPasting,
    dispatch,
    dispatchState,
  })

  const isMounted = useRef(true)

  useEffect(() => {
    return () => {
      // Dispatch resetStore action when component unmounts
      isMounted.current = false
      dispatchState({ type: 'resetStore' })
    }
  }, [dispatchState])

  useEffect(() => {
    isMounted.current = true
  }, [])

  const { invalidateCache } = useDatasetsDataCache()

  const { addPrefix, removePrefix } = usePageTitleContext()
  const { dispatch: pageHeaderDispatch } = useContext(PageHeaderContext)

  const submissionTemplate = useSelector(
    state => state.Datasets.submissionTemplatesById[defaultTemplateId],
  )

  const { template } = useTemplate({ templateId: defaultTemplateId })

  const { datasetUuid } = useQueryDatasetData({
    templateId: defaultTemplateId,
    binderRef,
    enabled: isMounted.current,
  })

  /**
   * If the dispatch from the Redux store has changed, reset URLAudits and cause store to re-render data.
   * - Not clear why this is necessary?
   */
  useDeepCompareEffect(() => {
    if (!template?.id) return

    UrlAudits.set([])
    UrlAudits.clearQueue()
    dispatch(DatasetActions.getTemplateDependencies(template.id, template))

    return () => {
      dispatch(DatasetActions.resetStore())
    }
  }, [dispatch, template])

  const requireSubmissionName = Boolean(submissionTemplate?.settings?.require_identifier)
  const datasetMeta = useSelector(state => state.Datasets.datasetMetadataById[dsId])
  const title = submissionTemplate?.name
  const requireApproval = Boolean(submissionTemplate?.settings?.require_approval)
  const [DATASETS_PAGE_ENABLED = false] = useSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!defaultTemplateId || !isMounted.current) return
    dispatchState({ type: 'setTemplateId', payload: defaultTemplateId })
  }, [defaultTemplateId])

  useEffect(() => {
    if (!datasetMeta?.updated_at && !dataset?.updated_at) return

    setUpdatedAt(dataset?.updated_at || datasetMeta?.updated_at)
  }, [dataset?.updated_at, datasetMeta?.updated_at])

  useEffect(() => {
    if (history.location.pathname.includes('create')) return

    addPrefix(submissionName || t`Untitled`)

    return () => {
      removePrefix()
    }
  }, [submissionName])

  useEffectPreventReload({ block: unsavedChanges })

  // Set page header configuration
  useEffect(() => {
    const folderName = submissionTemplate?.folder?.name

    if (!folderName && !title) return

    pageHeaderDispatch({
      type: 'setBreadcrumbs',
      payload: [
        ...(DATASETS_PAGE_ENABLED
          ? [{ label: t`Datasets`, path: '/' }]
          : [{ label: t`Templates`, path: '/codes/templates' }]),
        folderName ? { label: folderName } : null,
        {
          label: title,
          path: routeByParam({ url: ROUTES.TEMPLATE_DATA, params: { id: templateId } }),
        },
      ].filter(p => p),
    })

    return () => {
      pageHeaderDispatch({
        type: 'setBreadcrumbs',
        payload: [],
      })
    }
  }, [submissionTemplate?.folder?.name, title, templateId])

  const onUserTableInput = useCallback(() => {
    dispatchState({ type: 'setStatus', payload: { unsavedChanges: true } })
  }, [dispatchState])

  const dataContainerLength = Object.keys(dataContainer).length

  const handleTableInput = useCallback(() => {
    if (submissionTemplate?.integrations && dataContainerLength) {
      dispatchState({
        type: 'setIntegrationErrors',
        payload: validateIntegrationRequirements(
          submissionTemplate.integrations,
          dataContainer.getNonEmptyRows(),
        ),
      })
    }
    onUserTableInput()
  }, [dispatchState, submissionTemplate?.integrations, dataContainerLength, dataContainer])

  const onInvalidCellsUpdated = useCallback(
    errors => {
      dispatchState({ type: 'invalidCells', payload: { errors } })
    },
    [dispatchState],
  )

  const makeDatasetPayload = useCallback(() => {
    const tableData = dataContainer.getNonEmptyRows()

    if (!tableData.length) {
      return null
    }

    // Grab the field IDs on the first row and assume they are the same for all other rows.
    const headers = Object.values(tableData[0])
      .filter(cell => !cell.renderOnly && cell.field_id)
      .map(cell => cell.field_id)

    // Loop over all the data and create a two-dimensional array of arrays (CSV-esque data structure)
    const rows = tableData.map(row => {
      const values = Object.values(row)
        .filter(cell => !cell.renderOnly && cell.field_id)
        .map(cell => cell.cell_value)
      return values
    })

    return {
      datasetId: Number(dsId) || null,
      datasetUuid,
      templateId: Number(templateId),
      ...(dataset.commitRef && { commitRef: dataset.commitRef }),
      submissionName: submissionName || t`Untitled Submission`,
      requireApproval,
      headers,
      rows,
    }
  }, [dataContainer, dsId, dataset.commitRef, submissionName, templateId])

  const submitViaDataPlatform = useCallback(async () => {
    const payload = makeDatasetPayload()

    if (!payload) {
      dispatch(
        showSnackbar({
          message: t`Please enter some data to submit.`,
          type: 'ERROR',
        }),
      )
      return
    }

    try {
      const response = await api.post('/api/v1/account/dataset/submit', payload)

      dispatchState({ type: 'setStatus', payload: { unsavedChanges: false } })

      dispatch(
        showSnackbar({
          message: t`Your data has been submitted and is currently processing.`,
          type: 'INFO',
          duration: 5000,
        }),
      )

      history.push(`/codes/details/${response.data.datasetId}/data`)
      return response
    } catch (e) {
      dispatchState({ type: 'setStatus', payload: { isSubmitting: false } })
      // IF it is a validation error THEN display the default error message
      if (e?.status === 400 && e?.response?.data?.errors) {
        e.response.data.errors.forEach(err => {
          dispatch(
            showSnackbar({
              message: err.defaultErrorMessage,
              type: 'ERROR',
            }),
          )
        })
        return e
      }

      // IF it is another kind of error THEN display its message
      if (e?.status?.toString().startsWith(4) && e?.response?.data?.message) {
        dispatch(
          showSnackbar({
            message: e.response.data.message,
            type: 'ERROR',
          }),
        )
        return e
      }

      // At a minimum throw an error so we know something went wrong; don't swallow it
      throw e
    }
  }, [makeDatasetPayload, history])

  const onSubmit = useCallback(() => {
    const lastSelection = tableRef?.current?.getSelectedLast() || []
    const lastRow = lastSelection[0] ? lastSelection[0] : 0
    const lastColumn = lastSelection[1] ? lastSelection[1] : 0

    const lastCellClasses = tableRef?.current?.getCell(lastRow, lastColumn)?.classList

    if (!lastCellClasses?.contains('htError')) {
      dispatchState({ type: 'setStatus', payload: { isSubmitting: true, unsavedChanges: false } })

      /**
       * When we save a template, we need to invalidate the datasets and templates query
       */
      invalidateCache({ datasetId: Number(dsId) || null })
      queryClient.invalidateQueries([GetTemplatesQueryKey.TEMPLATES])
      return submitViaDataPlatform().catch(() => {
        dispatchState({ type: 'setStatus', payload: { unsavedChanges: true } })
      })
    }
  }, [submitViaDataPlatform])

  const saveViaDataPlatform = useCallback(async () => {
    const payload = makeDatasetPayload()
    dispatchState({ type: 'setStatus', payload: { unsavedChanges: false } })

    if (!payload) {
      dispatch(
        showSnackbar({
          message: t`Please enter some data to save.`,
          type: 'ERROR',
        }),
      )
      dispatchState({ type: 'setStatus', payload: { unsavedChanges: true } })
      return
    }

    try {
      const response = await api.post('/api/v1/account/dataset/draft', payload)

      // Build query data rows for the cache; same structure as endpoint response.
      const queryDataRows = dataContainer.getNonEmptyRows()?.map(row => ({
        cvid: row.cvid || '',
        data: Object.entries(row).reduce((memo, [fieldId, field]) => {
          memo[fieldId] = { value: field.cell_value }
          return memo
        }, {}),
      }))

      // Update the cache with the new data, this allows the back button to
      // restore the correct data.
      queryClient.setQueryData([KEY_DATASET_DATA_QUERY, dsId], oldData => ({
        ...oldData,
        commitRef: response.data.commitRef,
        rows: queryDataRows,
      }))

      // Update the dataContainer with the new data.
      const datasetRows = dataContainer.buildDatasetRowsFromData()
      const updatedDataset = {
        ...dataset,
        dataset_rows: datasetRows,
        ...(response.data.commitRef && { commitRef: response.data.commitRef }),
        updated_at: new Date(),
      }

      dataContainer.updateDatasetDataAndTemplateFields({
        binderRef,
        dataset: updatedDataset,
        transformedColumns: submissionTemplate?.transformedColumns,
      })

      dispatchState({
        type: 'setDataset',
        payload: {
          dataset: updatedDataset,
          subTitle,
          submissionName,
          templateId,
        },
      })

      dispatch(
        showSnackbar({
          message: t`Submission saved successfully`,
          type: 'SUCCESS',
          duration: 3000,
        }),
      )

      if (!dsId) {
        history.replace(`/codes/update/${templateId}/${response.data.datasetId}`)
      }
    } catch (e) {
      dispatchState({ type: 'setStatus', payload: { isSubmitting: false, unsavedChanges: true } })
      // IF it is a validation error THEN display the default error message
      if (e?.status === 400 && e?.response?.data?.errors) {
        e.response.data.errors.forEach(err => {
          dispatch(
            showSnackbar({
              message: err.defaultErrorMessage,
              type: 'ERROR',
            }),
          )
        })
        return e
      }

      // IF it is another kind of error THEN display its message
      if (e?.status?.toString().startsWith(4) && e?.response?.data?.message) {
        dispatch(
          showSnackbar({
            message: e.response.data.message,
            type: 'ERROR',
          }),
        )
        return e
      }

      // At a minimum throw an error so we know something went wrong; don't swallow it
      throw e
    }
  }, [
    dsId,
    makeDatasetPayload,
    dispatch,
    dispatchState,
    submissionName,
    templateId,
    queryClient,
    submissionTemplate,
  ])

  const onSave = useCallback(async () => {
    return !isSubmitting && saveViaDataPlatform()
  }, [saveViaDataPlatform, isSubmitting])

  useAutoSave(onSave, unsavedChanges, 300000)

  const dataFinishedLoading = useCallback(() => {
    dispatchState({ type: 'setStatus', payload: { unsavedChanges: false } })
  }, [dispatchState])

  const onErrorClick = useCallback(() => {
    moveToCellWithError({ tableRef, binderRef, errors })
  }, [tableRef, binderRef, errors])

  const setTableRefCallback = useCallback(
    ref => {
      tableRef.current = ref
    },
    [tableRef.current],
  )

  const setBinderRefCallback = useCallback(
    ref => {
      binderRef.current = ref
    },
    [binderRef.current],
  )

  const submissionNameMissingOrDefault =
    !submissionName || submissionName === t`Untitled Submission`
  const isSubmissionNameInValid =
    requireSubmissionName && (headerTitleInvalid || submissionNameMissingOrDefault)

  const submitDisabled =
    isSubmitting ||
    isPasting ||
    blockSubmit ||
    integrationErrors.length ||
    isSubmissionNameInValid ||
    !tableHasData

  const saveDisabled = blockSave || isPasting || integrationErrors.length || !tableHasData

  const debouncedEditableTitle = debounce(function debouncedEditableTitle(value) {
    if (headerTitleInvalid) setHeaderTitleInvalid(false)
    dispatchState({ type: 'setSubmissionName', payload: value })
  }, 300)

  const TOOLBAR_HEIGHT = '80px'

  const onBlurCallback = useCallback(_value => {
    dispatchState({ type: 'setUnblockSubmit' })
    setBlockSave(false)
  }, [])

  const onFocusCallback = useCallback(function onFocusCallback() {
    dispatchState({ type: 'setBlockSubmit' })
    setBlockSave(true)
  }, [])

  return (
    <motion.div
      className="create-dataset"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ height: 'calc(100vh - 62px' }}
    >
      <DatasetsHeader
        title={submissionName}
        status={Status.ADD_ROWS}
        templateId={defaultTemplateId}
        editableTitleCallback={debouncedEditableTitle}
        editableTitleRequired={requireSubmissionName}
        editableDefaultTitle={t`Untitled Submission`}
        titleValidationError={isSubmissionNameInValid}
        onBlurCallback={onBlurCallback}
        onFocusCallback={onFocusCallback}
      />
      <TableWrapper>
        <CreateDatasetToolbar
          isPending={datasetMeta?.is_pending}
          hasDataset={Boolean(datasetMeta && Object.keys(datasetMeta)?.length)}
          datasetId={dsId}
          onSave={onSave}
          onSubmit={onSubmit}
          tableRef={tableRef?.current}
          updatedAt={updatedAt}
          submitDisabled={submitDisabled}
          saveDisabled={saveDisabled}
          onErrorClick={onErrorClick}
          binderRef={binderRef}
          dataContainer={dataContainer}
        />
        <div
          id="HOTContainer"
          style={{ width: '100%', flex: 1, overflow: 'scroll', backgroundColor: '#f6f6f6' }}
          className="submission-table"
        >
          <div
            id="handsontable-container"
            className="handsontable-container"
            style={{ height: `calc(100% - ${TOOLBAR_HEIGHT})` }}
          >
            {!loading ? (
              <SubmissionTable
                dataContainer={dataContainer}
                dataset={dataset}
                template={submissionTemplate}
                hasUnsavedChanges={unsavedChanges}
                onUserChange={handleTableInput}
                onInvalidCellsUpdated={onInvalidCellsUpdated}
                setTableRef={setTableRefCallback}
                setBinderRef={setBinderRefCallback}
                tableRef={tableRef?.current}
                dataLoaded={dataFinishedLoading}
                setPasteStatus={setPasteStatus}
                setCopyStatus={setCopyStatus}
                setTableHasData={setTableHasData}
              />
            ) : (
              <Loader />
            )}
          </div>
        </div>
      </TableWrapper>
      <PromptSavedChanges hasUnsavedChanges={unsavedChanges} />
    </motion.div>
  )
}
